import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { api } from "../../../../api/client";
import type { ChangeRequestAttachment } from "../../../../api/types/changeRequestsTypes";
import useImpersonationBlockContext from "../../../../contexts/useImpersonationBlockContext";
import ChangeRequestAttachmentsList from "./ChangeRequestAttachmentsList";

interface Props {
  investorId: string;
  attachments: ChangeRequestAttachment[];
  onChange: (attachments: ChangeRequestAttachment[]) => void;
  onBusyChange: (busy: boolean) => void;
  disabled?: boolean;
}

const acceptedExtensions = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_ATTACHMENT_SIZE_MB = 25;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
const MAX_ATTACHMENTS_PER_REQUEST = 3;

const uploadAttachment = withErrorHandling(api.changeRequests.uploadAttachment);

const ChangeRequestAttachmentsField = ({ investorId, attachments, onChange, onBusyChange, disabled }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { isBlockedUnderImpersonation, showImpersonationBlockedDialog } = useImpersonationBlockContext();
  const [uploadingFileName, setUploadingFileName] = useState<string>();

  const isUploading = uploadingFileName !== undefined;

  const isAcceptedFile = (file: File) => {
    if (!acceptedExtensions.includes(getFileExtension(file.name))) {
      sendNotificationError(`${file.name}: only PDF, JPG and PNG files are supported`);
      return false;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      sendNotificationError(`${file.name}: the file is larger than ${MAX_ATTACHMENT_SIZE_MB} MB`);
      return false;
    }
    return true;
  };

  const uploadFiles = async (files: File[]) => {
    // Accumulate locally: the attachments captured when the drop started do not update across the awaits below.
    const uploaded = [...attachments];
    for (const file of files) {
      setUploadingFileName(file.name);
      const [attachment, error] = await uploadAttachment(investorId, file);
      if (error) {
        if (isBlockedUnderImpersonation(error)) {
          showImpersonationBlockedDialog();
          return;
        }
        logError(error, "[ChangeRequestAttachmentsField] uploadAttachment");
        sendNotificationError(error.message);
        continue;
      }

      uploaded.push(attachment);
      onChange([...uploaded]);
    }
  };

  const handleFilesAdd = async (files: File[]) => {
    const acceptedFiles = files.filter(isAcceptedFile);
    const freeSlots = MAX_ATTACHMENTS_PER_REQUEST - attachments.length;
    if (acceptedFiles.length > freeSlots) {
      sendNotificationError(`You can attach up to ${MAX_ATTACHMENTS_PER_REQUEST} files`);
    }

    const filesToUpload = acceptedFiles.slice(0, freeSlots);
    if (filesToUpload.length === 0) {
      return;
    }

    onBusyChange(true);
    await uploadFiles(filesToUpload);
    setUploadingFileName(undefined);
    onBusyChange(false);
  };

  const handleRemove = (attachment: ChangeRequestAttachment) => {
    onChange(attachments.filter((a) => a.dataCatalogId !== attachment.dataCatalogId));
  };

  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      <Typography variant="subtitle2">Attachments</Typography>
      <ChangeRequestAttachmentsList
        investorId={investorId}
        attachments={attachments}
        uploadingFileName={uploadingFileName}
        onRemove={isUploading || disabled ? undefined : handleRemove}
      />
      {attachments.length < MAX_ATTACHMENTS_PER_REQUEST && (
        <FilesDropArea
          acceptedFileExtensions={acceptedExtensions}
          allowMultiple
          disabled={disabled || isUploading}
          subtitle={`Max. ${MAX_ATTACHMENTS_PER_REQUEST} files, ${MAX_ATTACHMENT_SIZE_MB} MB each. Supported formats: PDF, JPG, PNG.`}
          onFilesAdd={handleFilesAdd}
        />
      )}
    </Stack>
  );
};

export default ChangeRequestAttachmentsField;
